/**
 * Product Card Hover V2 - Web Component
 *
 * Handles variant thumbnail hover interactions:
 * - Hovering thumbnail updates main card image
 * - Hovering thumbnail updates available sizes display
 * - Clicking thumbnail navigates to variant page
 * - Manages active state between thumbnails
 *
 * @see snippets/card-product.liquid for HTML structure
 * @see assets/component-card.css for styles
 */

class ProductCardHover extends HTMLElement {
  constructor() {
    super();

    // Get references to key elements
    this.card = this.closest('.card--product');
    this.mainImage = this.card?.querySelector('.card-media img');
    this.variantThumbs = this.querySelectorAll('.hover-variant-thumb');
    this.sizesContainer = this.querySelector('[data-sizes-container]');

    // Initialize if we have thumbnails
    if (this.variantThumbs.length > 0) {
      this.initVariantHover();
    }
  }

  /**
   * Initialize thumbnail hover and click handlers
   */
  initVariantHover() {
    this.variantThumbs.forEach(thumb => {
      // Mouse enter: Update main image and sizes
      thumb.addEventListener('mouseenter', () => {
        this.updateVariant(thumb);
      });

      // Click: Navigate to variant page
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.navigateToVariant(thumb);
      });

      // Keyboard support: Enter/Space key
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          this.navigateToVariant(thumb);
        }
      });
    });
  }

  /**
   * Update variant display (image and sizes)
   * @param {HTMLElement} thumb - The thumbnail button that was hovered
   */
  updateVariant(thumb) {
    // Remove active state from all thumbnails
    this.variantThumbs.forEach(t => t.classList.remove('active'));

    // Add active state to current thumbnail
    thumb.classList.add('active');

    // Update main image
    if (this.mainImage) {
      const newImageSrc = thumb.dataset.image;
      const color = thumb.dataset.color;

      this.mainImage.src = newImageSrc;
      this.mainImage.alt = color;

      // Update srcset if it exists
      if (this.mainImage.hasAttribute('srcset')) {
        const baseUrl = newImageSrc.split('?')[0];
        const srcset = [
          `${baseUrl}?width=165 165w`,
          `${baseUrl}?width=360 360w`,
          `${baseUrl}?width=540 540w`,
          `${baseUrl}?width=720 720w`
        ].join(', ');
        this.mainImage.srcset = srcset;
      }
    }

    // Update sizes
    if (this.sizesContainer) {
      const sizes = thumb.dataset.sizes.split(',');
      this.sizesContainer.innerHTML = sizes
        .map(size => `<span class="hover-size">${size}</span>`)
        .join('');
    }
  }

  /**
   * Navigate to variant page
   * @param {HTMLElement} thumb - The thumbnail button that was clicked
   */
  navigateToVariant(thumb) {
    const variantId = thumb.dataset.variantId;
    const productUrl = this.card?.querySelector('.card-media')?.href;

    if (productUrl && variantId) {
      // Remove existing variant parameter if present
      const cleanUrl = productUrl.split('?')[0];
      window.location.href = `${cleanUrl}?variant=${variantId}`;
    }
  }
}

// Register custom element
if (!customElements.get('product-card-hover')) {
  customElements.define('product-card-hover', ProductCardHover);
}
